import compact from 'lodash/compact';
import upperFirst from 'lodash/upperFirst';

import { BadInputError } from '@watershed/errors/BadInputError';
import { i18n } from '@watershed/intl';
/**
 * Adds a period to the end of a string, if the string doesn't already have
 * terminal punctuation.
 */
export function addStop(v: string): string {
  const trimmed = v.trim();
  return /[.!?]['"]?$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * Takes a list of stringish values, removes the falsy ones, and joins them all
 * with spaces.
 */
export function spaceJoin(
  ...args: Array<string | undefined | null | Array<string | undefined | null>>
): string {
  return compact(args.flat()).join(' ');
}

/**
 * Trims and lowercases a string.
 */
export function trimLower(x: string): string {
  return x.trim().toLowerCase();
}

/**
 * Lowercases the first letter of a string.
 */
export function lowerFirst(x: string): string {
  return x.charAt(0).toLowerCase() + x.slice(1);
}

export interface StringSortSpec {
  top?: Array<string>;
  bottom?: Array<string>;
}

/**
 * Sorts a list of strings according to a StringSortSpec. Any strings in `top`
 * will be at the top (in order); any in `bottom` wil be at the bottom (in
 * order); and any unspecified will be in between, in alphabetical order.
 */
export function sortStringsBySpec(
  values: Array<string>,
  sortSpec?: StringSortSpec
): Array<string> {
  const alphaSortedEntries = [...values].sort();
  if (!sortSpec?.top && !sortSpec?.bottom) {
    return alphaSortedEntries;
  }

  const top = sortSpec?.top ?? [];
  const bottom = sortSpec?.bottom ?? [];
  const topSet = new Set(top);
  const bottomSet = new Set(bottom);

  const result: Array<string> = [];

  for (const value of top) {
    if (values.includes(value)) {
      result.push(value);
    }
  }

  for (const value of alphaSortedEntries) {
    if (topSet.has(value) || bottomSet.has(value)) {
      continue;
    }
    result.push(value);
  }

  for (const value of bottom) {
    if (values.includes(value)) {
      result.push(value);
    }
  }

  return result;
}

export function toSentenceCase(s: string): string {
  const [firstWord, ...rest] = s.split(/(?=[A-Z])/);
  return [
    upperFirst(firstWord.trim()),
    ...rest.map((word) => word.trim().toLowerCase()),
  ].join(' ');
}

export function toSentenceCaseOrNull(s: string | null): string | null {
  return s === null ? s : toSentenceCase(s);
}

/**
 * Transforms a string to snake case. The main difference from _.snakeCase is
 * that it allows for multiple underscores in a row and doesn't trim
 * underscores at the start and end. Those differences are because it's meant
 * to be used as an input mask.
 */
export function snakeCaseInputMask(input: string): string {
  return input.toLowerCase().replaceAll(/[^a-z0-9_]/g, '_');
}

export function isSnakeCaseInput(input: string): boolean {
  return /^[a-z0-9_]*$/.test(input);
}

/**
 * Collapse all whitespace. Mostly useful for tests
 */
export function collapseAllWhitespace(
  input: string,
  replaceWith: string = '\n'
): string {
  return input.replace(/\s+/g, replaceWith);
}

export function removeAllWhitespace(input: string): string {
  return input.replace(/\s/g, '');
}

export function toBase64(str: string): string {
  return Buffer.from(str).toString('base64').replace('=', '');
}

export function fromBase64(str: string): string {
  return Buffer.from(str, 'base64').toString('utf-8');
}

export const KGCO2E = 'kgCO₂e';

export function firstNameLastInitial(name: string): string {
  if (!name.includes(' ')) {
    return name;
  }
  const [firstName, lastName] = name.split(' ');
  return `${firstName} ${lastName.charAt(0)}`;
}

// Also known as lcs() for people grepping for that.
// if two subsequences are equal length, function returns the first one
export function longestConsecutiveSubsequence(
  string1: string,
  string2: string
): string {
  const string1Length = string1.length;
  const string2Length = string2.length;
  const dp = new Array(string1Length + 1)
    .fill(0)
    .map(() => new Array(string2Length + 1).fill(0));
  let longestSubsequenceLength = 0;
  let longestSubsequenceEndIndex = 0;
  for (let i = 1; i <= string1Length; i++) {
    for (let j = 1; j <= string2Length; j++) {
      if (string1[i - 1] === string2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
        if (dp[i][j] > longestSubsequenceLength) {
          longestSubsequenceLength = dp[i][j];
          longestSubsequenceEndIndex = i;
        }
      }
    }
  }
  return string1.slice(
    longestSubsequenceEndIndex - longestSubsequenceLength,
    longestSubsequenceEndIndex
  );
}

export type Sortable = string | number | boolean | Date | undefined | null;

export function createNullableStringComparator({
  sortNumbersByValue,
}: {
  sortNumbersByValue?: boolean;
}): (a: Sortable, b: Sortable) => number {
  const collator = new Intl.Collator(i18n.locale, {
    numeric: sortNumbersByValue,
  });
  return (a: Sortable, b: Sortable) =>
    collator.compare((a ?? '').toString(), (b ?? '').toString());
}

export function formatCurrencyUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Extract the json-related substring from a string.
 * We assume there is only one json object in the input string and it is
 * surrounded by curly braces.
 */
export function extractJsonString(input: string): string | null {
  const startMarker = '```json';
  const endMarker = '```';
  const jsonStart = input.indexOf(startMarker) + startMarker.length;
  const jsonEnd = input.lastIndexOf(endMarker);
  if (jsonStart === -1 || jsonEnd === -1 || jsonEnd < jsonStart) {
    return null;
  }
  return input.slice(jsonStart, jsonEnd).trim();
}

/**
 * Sanitizes whitespace in a string:
 * - Collapses all whitespace (including line breaks) to single spaces
 * - Trims leading and trailing whitespace
 * - Removes any zero-width spaces
 * @param input string to sanitize
 * @returns sanitized string
 */
export function sanitizeWhitespace(input: string): string {
  const collapsed = input.replace(/\s+/g, ' ').trim();
  const noZeroWidth = collapsed.replace(/[\u200B-\u200F\uFEFF]/g, '');
  return noZeroWidth;
}

/**
 * Converts a percentage string like "75%" to a decimal string like "0.75"
 * This function performs the conversion using string manipulation to avoid
 * floating point precision issues.
 * @param input string to convert
 * @returns decimal string
 */
export function convertPercentageToDecimal(input: string): string {
  // Remove any whitespace and check if string matches pattern of digits.digits%
  let numStr = input.replace(/\s/g, '');
  if (!numStr.match(/^-?\d*\.?\d+%$/)) {
    throw new BadInputError('Invalid percentage string', {
      data: { input },
    });
  }

  const isNegative = numStr.startsWith('-');
  numStr = numStr.replace('%', '').replace('-', '');

  let result: string;
  if (numStr.includes('.')) {
    // Has a decimal point
    const [whole, fraction] = numStr.split('.');

    // Move decimal point two places left
    if (whole === '' || whole === '0') {
      // Cases like ".5%" or "0.5%" -> "0.005"
      result = `0.00${fraction}`;
    } else if (whole.length === 1) {
      // Case like "5.1%" -> "0.051"
      result = `0.0${whole}${fraction}`;
    } else if (whole.length === 2) {
      // Case like "75.1%" -> "0.751"
      result = `0.${whole}${fraction}`;
    } else {
      // Cases like "102.1%", "1234.5%" etc.
      const decimalPosition = whole.length - 2;
      result = `${whole.slice(0, decimalPosition)}.${whole.slice(decimalPosition)}${fraction}`;
    }
  } else {
    // No decimal point
    if (numStr === '0') {
      // Case "0%" -> "0.00"
      result = '0.00';
    } else if (numStr.length === 1) {
      // Case like "5%" -> "0.05"
      result = `0.0${numStr}`;
    } else if (numStr.length === 2) {
      // Case like "75%" -> "0.75"
      result = `0.${numStr}`;
    } else {
      // Cases like "102%", "1234%" etc.
      const decimalPosition = numStr.length - 2;
      result = `${numStr.slice(0, decimalPosition)}.${numStr.slice(decimalPosition)}`;
    }
  }

  // Remove trailing zeros
  result = result.replace(/\.?0+$/, '');
  return isNegative ? `-${result}` : result;
}
